'use client';

import { useMutation } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { saveLandingUtm } from '@/utils/analytics/landingUtm';
import { postWelloTrack } from '@/utils/analytics/welloTrack';
import { isBot } from '@/utils/isBot';

const WelloTrackerComponent = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isLogin = useAuthStore((state) => state.isLogin);
  const [osType, viewportType] = useDeviceInfoStore(
    useShallow((state) => [state.osType, state.viewportType] as const),
  );

  const { mutate: sendPageView } = useMutation({
    mutationFn: () => {
      const previousUrl = (() => {
        if (typeof window === 'undefined') return '';

        if (window.history.state && window.history.state.url) {
          return window.history.state.url;
        }

        return document.referrer || '';
      })();

      const fullUrl = `${pathname}${
        searchParams?.toString() ? `?${searchParams.toString()}` : ''
      }`;

      return postWelloTrack({
        eventName: 'pageView',
        dictionary: { url: fullUrl, osType, viewportType, previousUrl },
      });
    },
  });

  useEffect(() => {
    if (searchParams) {
      saveLandingUtm(searchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isBot()) return;
    if (isLogin !== undefined) {
      sendPageView();
    }
  }, [pathname, sendPageView, isLogin]);

  return <></>;
};

export const WelloTracker = () => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <WelloTrackerComponent />
      </Suspense>
    </ErrorBoundary>
  );
};
